import { ArrowLeft, Check } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { useCreateRole, useUpdateRole, useFetchPermissions } from '@/hooksApi';
import { toast } from '@/hooks/use-toast';
import { Role, PermissionName, RuleName } from '@/types';

// Permission dependency map (preserved from original RoleForm)
const PERMISSION_DEPENDENCIES: Record<string, string[]> = {
  [PermissionName.ORDER_AND_SHIPMENT]: [PermissionName.DASHBOARD],
  [PermissionName.WITHDRAW_REMITTANCE]: [PermissionName.FINANCE],
  [PermissionName.CREATE_ORDER]: [PermissionName.DASHBOARD, PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.UPDATE_ORDER]: [PermissionName.DASHBOARD, PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.BULK_IMPORT_ORDER]: [PermissionName.DASHBOARD, PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.DELETE_ORDER]: [PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.CANCEL_ORDER]: [PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.RESOLVE_OR_REQUEST_RTO]: [PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.REQUEST_PICK_UP]: [PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.CREATE_TICKET]: [PermissionName.DASHBOARD],
  [PermissionName.ADD_COMMENT]: [PermissionName.DASHBOARD],
  [PermissionName.SOLVE_TICKET]: [PermissionName.DASHBOARD],
  [PermissionName.FILE_CLAIM]: [PermissionName.DASHBOARD, PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.VIEW_CLAIMS]: [PermissionName.DASHBOARD, PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.PRODUCT_CATALOG]: [PermissionName.DASHBOARD, PermissionName.ORDER_AND_SHIPMENT],
  [PermissionName.CARRIER_MANAGEMENT]: [PermissionName.DASHBOARD],
};

interface Props {
  roleUID: string; // '' for create, actual uid for edit
  roleData?: Role;
  onClose: () => void;
}

const RoleFormInline = ({ roleUID, roleData, onClose }: Props) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [selectedPermissions, setSelectedPermissions] = useState<string[]>([]);
  const [dependencyWarning, setDependencyWarning] = useState('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  const { mutate: createRole, isLoading: isCreating } = useCreateRole();
  const { mutate: updateRole, isLoading: isUpdating } = useUpdateRole();
  const { data: permissionsData } = useFetchPermissions();

  const isEditMode = roleUID !== '';
  const isSaving = isCreating || isUpdating;

  // Populate form for edit mode
  useEffect(() => {
    if (roleData) {
      setName(roleData.name || '');
      setDescription(roleData.description || '');
      const initialPermissionUids = roleData.permissions?.map((p) => p.uid) ?? [];
      setSelectedPermissions(initialPermissionUids);
    } else {
      setName('');
      setDescription('');
      setSelectedPermissions([]);
      setFieldErrors({});
      setDependencyWarning('');
    }
  }, [roleData]);

  // Permission dependency logic (preserved from original RoleForm)
  const getPrerequisites = (permissionName: string): string[] => {
    const prerequisites = PERMISSION_DEPENDENCIES[permissionName] || [];
    const allPrerequisites = new Set<string>(prerequisites);
    prerequisites.forEach((prereq) => {
      const nestedPrereqs = getPrerequisites(prereq);
      nestedPrereqs.forEach((p) => allPrerequisites.add(p));
    });
    return Array.from(allPrerequisites);
  };

  const handlePermissionToggle = (permissionUid: string) => {
    const isSelected = selectedPermissions.includes(permissionUid);
    let updatedList = isSelected
      ? selectedPermissions.filter((uid) => uid !== permissionUid)
      : [...selectedPermissions, permissionUid];

    updatedList = Array.from(new Set(updatedList));

    // Check dependencies (preserved from original RoleForm)
    const allMissingDependencies: { permission: string; prerequisites: string[] }[] = [];

    updatedList.forEach((uid) => {
      const perm = permissionsData?.object?.find((p) => p.uid === uid);
      if (!perm) return;

      const prerequisites = getPrerequisites(perm.name);
      const missingPrerequisites: string[] = [];

      prerequisites.forEach((prereqName) => {
        const prereqPermission = permissionsData?.object?.find((p) => p.name === prereqName);
        if (prereqPermission && !updatedList.includes(prereqPermission.uid)) {
          missingPrerequisites.push(t(`permission.${prereqName}`));
        }
      });

      if (missingPrerequisites.length > 0) {
        allMissingDependencies.push({
          permission: t(`permission.${perm.name}`),
          prerequisites: missingPrerequisites,
        });
      }
    });

    if (allMissingDependencies.length > 0) {
      const permissionsWithIssues = allMissingDependencies.map((dep) => dep.permission);
      const allMissingPrereqs = new Set<string>();
      allMissingDependencies.forEach((dep) => {
        dep.prerequisites.forEach((prereq) => allMissingPrereqs.add(prereq));
      });

      const andSeparator = t('and');
      const permissionsListStr = permissionsWithIssues.join(`" ${andSeparator} "`);
      const prerequisitesList = Array.from(allMissingPrereqs).join(` ${andSeparator} `);

      const translationKey =
        allMissingPrereqs.size === 1
          ? 'requires_the_following_permissions_one'
          : 'requires_the_following_permissions_other';

      setDependencyWarning(
        t(translationKey, {
          permissions: permissionsListStr,
          prerequisites: prerequisitesList,
        }),
      );
    } else {
      setDependencyWarning('');
    }

    setSelectedPermissions(updatedList);
    setFieldErrors((prev) => ({ ...prev, permissions: '' }));
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!name.trim()) {
      errors.name = t('this_field_is_required');
    } else if (!/^[a-z0-9_]*$/.test(name)) {
      errors.name = t('only_english_letters_numbers_and_underscore_are_allowed');
    } else if (/[A-Z]/.test(name)) {
      errors.name = t('letters_must_be_lowercase');
    } else {
      const presetRoles = Object.values(RuleName);
      if (presetRoles.includes(name.toLowerCase() as RuleName)) {
        errors.name = t('role_name_cannot_be_preset');
      }
    }

    if (!description.trim()) {
      errors.description = t('this_field_is_required');
    } else if (
      !/^[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF\uFB50-\uFDFF\uFE70-\uFEFF0-9\s._-]+$/.test(
        description,
      )
    ) {
      errors.description = t('only_arabic_letters_numbers_and_symbols_are_allowed');
    }

    if (selectedPermissions.length === 0) {
      errors.permissions = t('please_select_at_least_one_permission');
    }

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;
    if (dependencyWarning) return;

    const payload = {
      name,
      description,
      permission_uids: selectedPermissions,
    };

    if (isEditMode) {
      updateRole(
        { uid: roleUID, ...payload },
        {
          onSuccess: () => {
            onClose();
            queryClient.invalidateQueries(['teamMember-roles']);
            queryClient.invalidateQueries(['teamMember-users']);
            toast({ title: String(t('role_updated')) });
          },
        },
      );
    } else {
      createRole(payload, {
        onSuccess: () => {
          onClose();
          queryClient.invalidateQueries(['teamMember-roles']);
          toast({ title: String(t('role_created')) });
        },
      });
    }
  };

  const inputClass = (field: string) =>
    `w-full h-10 px-3 rounded-lg border text-sm text-sd-foreground focus:outline-none focus:ring-2 focus:ring-sd-ring ${
      fieldErrors[field]
        ? 'border-red-500 bg-red-50'
        : 'border-sd-input bg-sd-input-bg'
    }`;

  return (
    <div className='rounded-2xl border border-sd-border bg-sd-card shadow-sm'>
      {/* Header */}
      <div className='px-6 py-4 border-b border-sd-border flex items-center gap-3'>
        <button
          onClick={onClose}
          className='text-sd-muted-foreground hover:text-sd-foreground transition-colors'
        >
          <ArrowLeft className='h-4 w-4' />
        </button>
        <h2 className='text-sm font-semibold text-sd-foreground m-0'>
          {isEditMode ? t('edit_role') : t('new_role')}
        </h2>
      </div>

      {/* Content */}
      <div className='p-6 space-y-6'>
        <div className='grid grid-cols-1 sm:grid-cols-2 gap-4'>
          <div className='space-y-1.5'>
            <label className='text-xs font-medium text-sd-muted-foreground'>
              {t('english_name')} *
            </label>
            <input
              type='text'
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                setFieldErrors((prev) => ({ ...prev, name: '' }));
              }}
              placeholder={String(t('eg_manager'))}
              className={inputClass('name')}
            />
            {fieldErrors.name && (
              <p className='text-xs text-red-500 mt-0.5'>{fieldErrors.name}</p>
            )}
          </div>
          <div className='space-y-1.5'>
            <label className='text-xs font-medium text-sd-muted-foreground'>
              {t('arabic_name')} *
            </label>
            <input
              type='text'
              value={description}
              onChange={(e) => {
                setDescription(e.target.value);
                setFieldErrors((prev) => ({ ...prev, description: '' }));
              }}
              placeholder={String(t('eg_manager_ar'))}
              dir='rtl'
              className={inputClass('description')}
            />
            {fieldErrors.description && (
              <p className='text-xs text-red-500 mt-0.5'>{fieldErrors.description}</p>
            )}
          </div>
        </div>

        <div className='border-t border-sd-border' />

        {/* Dependency Warning */}
        {dependencyWarning && (
          <div className='rounded-lg border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-800'>
            {dependencyWarning}
          </div>
        )}

        {/* Access Areas */}
        <div>
          <p className='text-xs font-semibold text-sd-muted-foreground uppercase tracking-wider mb-3'>
            {t('access_areas')} *
          </p>
          {fieldErrors.permissions && (
            <p className='text-xs text-red-500 mb-2'>{fieldErrors.permissions}</p>
          )}
          <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
            {permissionsData?.object?.map((permission) => {
              const selected = selectedPermissions.includes(permission.uid);
              return (
                <button
                  key={permission.uid}
                  type='button'
                  onClick={() => handlePermissionToggle(permission.uid)}
                  className={`flex items-center gap-2.5 px-3 py-2.5 rounded-lg border text-sm text-left transition-colors ${
                    selected
                      ? 'border-sd-primary bg-sd-primary/10 text-sd-foreground'
                      : 'border-sd-input bg-sd-background text-sd-muted-foreground hover:bg-sd-muted'
                  }`}
                >
                  <div
                    className={`h-4 w-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                      selected ? 'bg-sd-primary border-sd-primary' : 'border-sd-input'
                    }`}
                  >
                    {selected && <Check className='h-3 w-3 text-sd-primary-foreground' />}
                  </div>
                  {permission.name ? t(`permission.${permission.name}`) : '---'}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Footer */}
      <div className='px-6 py-4 border-t border-sd-border flex justify-end gap-3'>
        <button
          onClick={onClose}
          className='px-4 py-2.5 rounded-lg border border-sd-input bg-sd-background text-sm font-medium hover:bg-sd-muted transition-colors text-sd-foreground'
        >
          {t('cancel')}
        </button>
        <button
          onClick={handleSave}
          disabled={isSaving || (selectedPermissions.length > 0 && dependencyWarning !== '')}
          className='px-6 py-2.5 rounded-lg bg-sd-primary text-sd-primary-foreground text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50'
        >
          {isEditMode ? t('update_role') : t('save_role')}
        </button>
      </div>
    </div>
  );
};

export default RoleFormInline;
